import { styled } from "@mui/material";
import Switch from "@mui/material/Switch";
import { max } from "lodash";
import { joiEmailSchema } from "../../../../utils/utils";
import * as Yup from "yup";

export const initialValues = {
  businessName: "",
  businessType: "",
  yearsinOperation: "",
  businessAddress: "",
  businessCity: "",
  businessState: "",
  businessZipCode: "",
  businessCountry: "",
  businessPhone: "",
  businessPhoneCC: "+1",
  businessEmail: "",
  businessWebsite: "",
  businessRegistrationNumber: "",
  operationHours: "",
  licenseDocument: "",
  serviceCoverageArea: [],
  primaryContactName: "",
  primaryContactEmail: "",
  primaryContactPhone: "",
  primaryContactPhoneCC: "+1",
  primaryContactRole: "",
  isEmergencyServiceAvailable: false,
  needTypes: [],
  serviceCategories: [],
  serviceDescription: "",
  pricingDocument: "",
  serviceFiles: [],
  staffStrength: "",
  agreedToTerms: false,
  emergencyContactPhone: "",
  emergencyContactPhoneCC: "+1",
};

export const validationSchema = Yup.object().shape({
  businessName: Yup.string()
    .matches(
      /^[a-zA-Z0-9\s]*$/,
      "Business name can only contain letters, numbers and spaces"
    )
    .max(50, "Business name cannot exceed 50 characters")
    .required("Business name is required"),
  businessType: Yup.string().required("Business type is required"),
  yearsinOperation: Yup.number()
    .typeError("Years in operation must be a number")
    .integer("Years in operation must be a whole number")
    .min(0, "Years in operation must be between 0 and 9999")
    .max(9999, "Years in operation must be between 0 and 9999")
    .nullable(),
  businessAddress: Yup.string()
    .max(255, "Address cannot exceed 255 characters")
    .required("Address is required"),
  businessCity: Yup.string()
    .matches(/^[a-zA-Z0-9\s]*$/, "City can only contain letters, and spaces")
    .max(50, "City cannot exceed 50 characters")
    .required("City is required"),
  businessState: Yup.string()
    .matches(/^[a-zA-Z\s]*$/, "State can only contain letters, and spaces")
    .max(50, "State cannot exceed 50 characters")
    .required("State is required"),
  businessZipCode: Yup.string()
    .matches(/^\d{5}(?:[-\s]\d{4})?$/, "Invalid zip code")
    .required("Zip code is required"),
  businessCountry: Yup.string()
    .matches(/^[a-zA-Z\s]*$/, "Country can only contain letters, and spaces")
    .max(50, "Country cannot exceed 50 characters")
    .required("Country is required"),
  businessPhone: Yup.string()
    .matches(/^\+?[0-9]{7,15}$/, "Invalid phone number")
    .min(10, "Business phone must be at least 10 digits")
    .required("Business phone is required"),

  businessEmail: Yup.string()
    .required("Business email is required")
    .email("Invalid email address")
    .max(150, "Email cannot exceed 150 characters")
    .test("joi-email-check", "Invalid email format", function (value) {
      if (!value) return false;
      const { error } = joiEmailSchema.validate(value);
      return !error;
    }),
  businessWebsite: Yup.string()
    .url("Invalid URL")
    .max(255, "Website URL cannot exceed 255 characters")
    .nullable(),
  businessRegistrationNumber: Yup.string()
    .matches(
      /^[a-zA-Z0-9]+$/,
      "Invalid registration number, should contain only letters and digits"
    )
    .max(30, "Registration number cannot exceed 30 characters")
    .nullable(),
  operationHours: Yup.string()
    .max(255, "Operation hours cannot exceed 255 characters")
    .nullable(),
  serviceCoverageArea: Yup.array().of(Yup.string()).optional(),
  primaryContactName: Yup.string()
    .matches(
      /^[a-zA-Z\s]*$/,
      "Primary contact name can only contain letters, and spaces"
    )
    .max(50, "Primary contact name cannot exceed 50 characters")
    .required("Primary contact name is required"),
  primaryContactEmail: Yup.string()
    .required("Primary contact email is required")
    .email("Invalid email address")
    .max(150, "Email cannot exceed 150 characters")
    .matches(
      /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Enter a valid email"
    )
    .test("joi-email-check", "Invalid email format", function (value) {
      if (!value) return false;
      const { error } = joiEmailSchema.validate(value);
      return !error;
    }),
  primaryContactPhone: Yup.string()
    .matches(/^\+?[0-9]{7,15}$/, "Invalid phone number")
    .min(10, "Primary contact phone must be at least 10 digits")
    .required("Primary contact phone is required"),
  primaryContactRole: Yup.string()
    .matches(
      /^[a-zA-Z\s]*$/,
      "Primary contact role can only contain letters, and spaces"
    )
    .max(50, "Primary contact role cannot exceed 50 characters")
    .required("Primary contact role is required"),
  isEmergencyServiceAvailable: Yup.boolean(),
  needTypes: Yup.array().of(Yup.string()).min(1, "At least one need type is required"),
  serviceCategories: Yup.array()
    .of(
      Yup.object({
        category: Yup.string().required("Category name is required"),
        subcategories: Yup.array()
          .of(Yup.string())
          .min(1, "At least one service is required"),
      })
    )
    .min(1, "At least one service category is required"),
  serviceDescription: Yup.string()
    .max(500, "Service description cannot exceed 500 characters")
    .nullable(),
  staffStrength: Yup.number()
    .min(0, "Staff strength cannot be negative")
    .max(99999, "Staff strength cannot exceed 99999")
    .typeError("Staff strength must be a number")
    .integer("Staff strength must be a whole number")
    .nullable(),
  agreedToTerms: Yup.boolean().oneOf(
    [true],
    "You must agree to the terms and conditions"
  ),
emergencyContactPhone: Yup.string().when("isEmergencyServiceAvailable", {
    is: true,
    then: (schema) =>
      schema
        .matches(/^\+?[0-9]{7,15}$/, "Invalid phone number")
        .min(10, "Emergency contact phone must be at least 10 digits")
        .required("Emergency contact phone is required"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  licenseDocument: Yup.mixed()
    .nullable()
    .test("is-single-file", "Only one file is allowed", (value) => {
      if (!value || (typeof value === "string" && value.length === 0))
        return true;
      if (value instanceof FileList) return value.length === 1;
      return !Array.isArray(value);
    })
    .test("fileSize", "File size must be 10MB or less", (value) => {
      if (!value || (typeof value === "string" && value.length === 0))
        return true;
      const getFile = (v: any) =>
        v instanceof FileList ? v.item(0) : Array.isArray(v) ? v[0] : v;
      const file: any = getFile(value);
      if (!file) return true;
      const maxSize = 10 * 1024 * 1024; // 10MB
      return file.size <= maxSize;
    })
    .test(
      "fileFormat",
      "Only PDF, DOC, DOCX, PNG, JPG, and JPEG files are allowed",
      (value) => {
        if (!value || (typeof value === "string" && value.length === 0))
          return true;
        const getFile = (v: any) =>
          v instanceof FileList ? v.item(0) : Array.isArray(v) ? v[0] : v;
        const file: any = getFile(value);
        if (!file) return true;
        const validTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "image/png",
          "image/jpeg",
        ];
        const validExts = ["pdf", "doc", "docx", "png", "jpg", "jpeg"];
        const hasValidType = file.type ? validTypes.includes(file.type) : false;
        const ext = (file.name || "").split(".").pop()?.toLowerCase() || "";
        const hasValidExt = validExts.includes(ext);
        return hasValidType || hasValidExt;
      }
    ),
  pricingDocument: Yup.mixed()
    .nullable()
    .test("is-single-file", "Only one file is allowed", (value) => {
      if (!value || (typeof value === "string" && value.length === 0))
        return true;
      if (value instanceof FileList) return value.length === 1;
      return !Array.isArray(value);
    })
    .test("fileSize", "File size must be 10MB or less", (value) => {
      if (!value || (typeof value === "string" && value.length === 0))
        return true;
      const getFile = (v: any) =>
        v instanceof FileList ? v.item(0) : Array.isArray(v) ? v[0] : v;
      const file: any = getFile(value);
      if (!file) return true;
      const maxSize = 10 * 1024 * 1024;
      return file.size <= maxSize;
    })
    .test(
      "fileFormat",
      "Only PDF, DOC, DOCX, PNG, JPG, and JPEG files are allowed",
      (value) => {
        if (!value || (typeof value === "string" && value.length === 0))
          return true;
        const getFile = (v: any) =>
          v instanceof FileList ? v.item(0) : Array.isArray(v) ? v[0] : v;
        const file: any = getFile(value);
        if (!file) return true;
        const validTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "image/png",
          "image/jpeg",
        ];
        const validExts = ["pdf", "doc", "docx", "png", "jpg", "jpeg"];
        const hasValidType = file.type ? validTypes.includes(file.type) : false;
        const ext = (file.name || "").split(".").pop()?.toLowerCase() || "";
        const hasValidExt = validExts.includes(ext);
        return hasValidType || hasValidExt;
      }
    ),
  serviceFiles: Yup.array()
    .transform((value, originalValue) => {
      if (originalValue instanceof FileList) return Array.from(originalValue);
      return originalValue;
    })
    .nullable()
    .max(1, "Only one file is allowed")
    .of(
      Yup.mixed()
        .test("fileSize", "File size must be 10MB or less", (file) => {
          if (!file) return true;
          const maxSize = 10 * 1024 * 1024;
          return (file as any).size <= maxSize;
        })
        .test(
          "fileFormat",
          "Only PDF, DOC, DOCX, PNG, JPG, and JPEG files are allowed",
          (file) => {
            if (!file) return true;
            const validTypes = [
              "application/pdf",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              "image/png",
              "image/jpeg",
            ];
            const validExts = ["pdf", "doc", "docx", "png", "jpg", "jpeg"];
            const hasValidType = (file as any).type
              ? validTypes.includes((file as any).type)
              : false;
            const ext =
              ((file as any).name || "").split(".").pop()?.toLowerCase() || "";
            const hasValidExt = validExts.includes(ext);
            return hasValidType || hasValidExt;
          }
        )
    ),
});

export const compactInputProps = {
  sx: {
    height: "42px",
    "& .MuiInputBase-root": {
      height: "42px",
    },
    "& input": {
      padding: "10px 14px !important",
      height: "42px",
      boxSizing: "border-box",
    },
    "& .MuiSelect-select": {
      padding: "10px 14px",
      height: "42px",
      display: "flex",
      alignItems: "center",
    },
    "& fieldset": {
      height: "100%",
    },
  },
};

export const AntSwitch = styled(Switch, {
  shouldForwardProp: (prop) => prop !== "checkedColor", // prevent MUI from passing it to DOM
})<any>(({ theme, checkedColor = "#15585D" }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: checkedColor ?? "#1890ff",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));
